import { useEffect, useRef, useState } from "react";

export type SaveToastType = "grocery" | "meal_plan" | "recipe";

interface ToastPayload {
  id: number;
  type: SaveToastType;
}

const COPY: Record<SaveToastType, { line1: string; line2Prefix: string; line2Suffix: string }> = {
  grocery: {
    line1: "Grocery list saved!",
    line2Prefix: "Find it anytime in your ",
    line2Suffix: " tab",
  },
  meal_plan: {
    line1: "Meal plan saved with grocery list!",
    line2Prefix: "Find both anytime in your ",
    line2Suffix: " tab",
  },
  recipe: {
    line1: "Recipe saved!",
    line2Prefix: "Find it anytime in your ",
    line2Suffix: " tab",
  },
};

const VISIBLE_MS = 6000;
const ANIM_MS = 300;

let counter = 0;

/** Fire a one-time saved toast from anywhere. */
export function showSavedToast(type: SaveToastType) {
  counter += 1;
  window.dispatchEvent(
    new CustomEvent<ToastPayload>("kin:saved-toast", {
      detail: { id: counter, type },
    })
  );
}

/** Navigate to the My Kin tab from anywhere. */
export function navigateToMyKin() {
  window.dispatchEvent(new CustomEvent("kin:navigate-tab", { detail: { tab: "mykin" } }));
}

const SavedToast = () => {
  const [toast, setToast] = useState<ToastPayload | null>(null);
  const [leaving, setLeaving] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const removeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onShow = (e: Event) => {
      const detail = (e as CustomEvent<ToastPayload>).detail;
      if (!detail) return;
      // Reset any pending timers
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (removeTimer.current) clearTimeout(removeTimer.current);
      setLeaving(false);
      setToast(detail);
      hideTimer.current = setTimeout(() => startDismiss(), VISIBLE_MS);
    };
    window.addEventListener("kin:saved-toast", onShow as EventListener);
    return () => {
      window.removeEventListener("kin:saved-toast", onShow as EventListener);
      if (hideTimer.current) clearTimeout(hideTimer.current);
      if (removeTimer.current) clearTimeout(removeTimer.current);
    };
  }, []);

  const startDismiss = () => {
    setLeaving(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    removeTimer.current = setTimeout(() => {
      setToast(null);
      setLeaving(false);
    }, ANIM_MS);
  };

  if (!toast) return null;

  const copy = COPY[toast.type];

  const handleMyKinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigateToMyKin();
    startDismiss();
  };

  return (
    <div
      onClick={startDismiss}
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: "80px",
        transform: `translateX(-50%) translateY(${leaving ? "20px" : "0"})`,
        opacity: leaving ? 0 : 1,
        transition: `opacity ${ANIM_MS}ms ${leaving ? "ease-in" : "ease-out"}, transform ${ANIM_MS}ms ${leaving ? "ease-in" : "ease-out"}`,
        width: "90%",
        maxWidth: "400px",
        backgroundColor: "hsl(var(--charcoal, 245 38% 18%))",
        color: "#FFFFFF",
        padding: "16px 20px",
        borderRadius: "14px",
        boxShadow: "0 8px 24px rgba(28, 26, 62, 0.2)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        gap: "12px",
        fontFamily: "var(--font-body, 'Plus Jakarta Sans', sans-serif)",
        cursor: "pointer",
        animation: leaving ? undefined : `kinToastIn ${ANIM_MS}ms ease-out`,
      }}
    >
      <style>{`
        @keyframes kinToastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          backgroundColor: "hsl(var(--soft-violet, 240 70% 87%))",
          color: "hsl(var(--charcoal, 245 38% 18%))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: "14px",
          fontWeight: 700,
        }}
      >
        ✓
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
        <div style={{ fontSize: "15px", fontWeight: 700, lineHeight: 1.3 }}>{copy.line1}</div>
        <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.7)", lineHeight: 1.4 }}>
          {copy.line2Prefix}
          <button
            type="button"
            onClick={handleMyKinClick}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
              fontWeight: 700,
              color: "hsl(var(--lavender, 250 80% 88%))",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            My Kin
          </button>
          {copy.line2Suffix}
        </div>
      </div>
    </div>
  );
};

export default SavedToast;
